import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  Search,
  Plus,
  Edit,
  UserPlus,
  UserRoundPlus,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getMasterCompanyList } from "@/redux/Masters/company/actions";
import { DataTable } from "@/components/ui/table";
import CompanySetup from "@/components/CompanySetup/CompanySetup";
import RequestCompanies from "./RequestCompanies";
import TablePagination from "@/components/common/TablePagination";
import ColumnVisibilityDropdown from "@/components/common/ColumnVisibilityDropdown";
import TableText from "@/components/common/TableText";
import PasswordCell from "@/components/common/PasswordCell";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const DEFAULT_PAGE_SIZE = 10;

const CompaniesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { masterCompanyList, companyPagination, masterCompanyListError } =
    useSelector((state) => state.masterCompany);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSignupRequests, setShowSignupRequests] = useState(false);
  const [showCompanyWizard, setShowCompanyWizard] = useState(false);
  const [showEditCompanyWizard, setShowEditCompanyWizard] = useState(false);

  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const columns = useMemo(
    () => [
      {
        key: "company",
        label: "Company",
        minWidth: 240,
        sticky: "left",
        render: (row) => {
          const name = row.COMPANYNAME || "-";
          const logo = row.COMPANYLOGO;

          const initials = name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0])
            .join("")
            .toUpperCase();

          return (
            <div
              onClick={() => handleViewCompany(row)}
              className="flex items-center gap-4 cursor-pointer group"
            >
              <Avatar className="w-11 h-11 rounded-xl">
                <AvatarImage
                  src={
                    logo && logo !== "https://api.shipez.ai/" ? logo : undefined
                  }
                  alt={name}
                  className="object-contain"
                />

                <AvatarFallback className="bg-teal-500 text-white font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>

              <p className="font-semibold text-sm text-slate-900 dark:text-slate-400 group-hover:text-blue-500 transition-colors">
                {name}
              </p>
            </div>
          );
        },
      },
      {
        key: "profileCategory",
        label: "Type",
        render: (row) => (
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-400">
            {row.PROFILE_CATEGORY || "-"}
          </span>
        ),
      },
      {
        key: "name",
        label: "Name",
        render: (row) => (
          <TableText
            title={`${row.PROFILE_FIRSTNAME} ${row.PROFILE_LASTNAME}`}
          />
        ),
      },
      {
        key: "email",
        label: "Email",
        render: (row) => (
          <TableText title={row.PROFILE_EMAILID} maxWidth="240px" />
        ),
      },
      {
        key: "username",
        label: "Username",
        render: (row) => <TableText title={row.PROFILE_USERNAME} />,
      },
      {
        key: "profilePassword",
        label: "Password",
        render: (row) => <PasswordCell password={row.PROFILE_PASSWORD} />,
      },
      {
        key: "profilePhone",
        label: "Phone",
        render: (row) => <TableText title={row.PROFILE_PHONE} />,
      },
      {
        key: "isActive",
        label: "Status",
        render: (row) => (
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              row.ISACTIVE
                ? "bg-emerald-500/20 text-emerald-500"
                : "bg-red-500/20 text-red-500"
            }`}
          >
            {row.ISACTIVE ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        key: "actions",
        label: "Actions",
        sticky: "right",
        width: 100,
        render: (row) => (
          <div className="flex">
            <button
              onClick={() => handleEditCompany(row)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <Edit className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  const [visibleColumns, setVisibleColumns] = useState(() =>
    columns.map((col) => col.key),
  );

  const filteredColumns = useMemo(
    () => columns.filter((col) => visibleColumns.includes(col.key)),
    [columns, visibleColumns],
  );

  const fetchCompanies = useCallback(
    async ({ page = 1, search = "", pageLimit = DEFAULT_PAGE_SIZE } = {}) => {
      try {
        setLoading(true);

        const response = await dispatch(
          getMasterCompanyList({
            IPageNo: page,
            IPageRecords: pageLimit,
            SearchKeyword: search,
            FilterType: "ALL",
          }),
        );

        return response;
      } catch (err) {
        console.error("Fetch companies error:", err);
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    fetchCompanies({
      page: currentPage,
      pageLimit: pageSize,
      search: debouncedSearch,
    });
  }, [currentPage, pageSize, debouncedSearch, fetchCompanies]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();

      if (trimmed === "" || trimmed.length >= 2) {
        setDebouncedSearch(trimmed);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (masterCompanyListError) {
      toast.error(masterCompanyListError);

      dispatch({
        type: CLEAR_MASTER_COMPANY_ERROR,
      });
    }
  }, [masterCompanyListError, dispatch]);

  const handleEditCompany = (company) => {
    setSelectedCompanyId(company?.COMPANYID);
    setShowEditCompanyWizard(true);
  };

  const totalPages = companyPagination?.totalPage || 1;

  const totalRecordCount = companyPagination?.totalRecord ?? 0;

  const handleViewCompany = (company) => {
    navigate(`/admin/companies/${company?.COMPANYID}`, {
      state: { isMaster: true },
    });
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 p-6">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              {showSignupRequests ? (
                <UserPlus className="w-7 h-7 text-amber-500" />
              ) : (
                <Building2 className="w-7 h-7 text-teal-500" />
              )}
              {showSignupRequests ? "Company Signup Requests" : "Companies"}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {showSignupRequests
                ? "Review and manage pending company signup requests"
                : "Manage all registered companies and subscriptions"}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSignupRequests((prev) => !prev)}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              {showSignupRequests ? (
                <>
                  <Building2 className="w-4 h-4" />
                  Company List
                </>
              ) : (
                <>
                  <UserRoundPlus className="w-4 h-4" />
                  Signup Requests
                </>
              )}
            </button>

            <button
              onClick={() => setShowCompanyWizard(true)}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Company
            </button>
          </div>
        </div>

        {/* Main content */}
        {!showSignupRequests ? (
          <div className="space-y-6">
            {/* Search + column toggle */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="relative w-full md:w-96">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search companies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                {/* FIX: now shows the real server-side total, not the
                    current page slice filtered client-side */}
                <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
                  <span className="font-bold text-slate-500 dark:text-white">
                    {totalRecordCount}
                  </span>{" "}
                  companies found
                </div>
              </div>

              <ColumnVisibilityDropdown
                columns={columns}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                storageKey="master-company-table-columns"
              />
            </div>

            {/* Error banner */}
            {error && (
              <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-500">
                {error}
              </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <DataTable
                data={masterCompanyList || []}
                columns={filteredColumns}
                loading={loading}
                emptyText="No companies found"
                Icon={Building2}
              />

              <div className="p-4">
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalRecords={companyPagination?.totalRecord}
                  currentRecord={companyPagination?.currentRecord}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </div>
            </div>
          </div>
        ) : (
          <RequestCompanies />
        )}
      </div>

      <CompanySetup
        isEdit={false}
        isOpen={showCompanyWizard}
        onClose={() => setShowCompanyWizard(false)}
        onSuccess={() =>
          fetchCompanies({
            page: currentPage,
            pageLimit: pageSize,
            search: searchTerm,
          })
        }
      />

      <CompanySetup
        isEdit={true}
        companyId={selectedCompanyId}
        isOpen={showEditCompanyWizard}
        onClose={() => {
          setShowEditCompanyWizard(false);
          setSelectedCompanyId(null);
        }}
        onSuccess={() =>
          fetchCompanies({
            page: currentPage,
            pageLimit: pageSize,
            search: searchTerm,
          })
        }
      />
    </div>
  );
};

export default CompaniesPage;
